/**
 * Tournament family — Swiss AND arena (they share every message type on this
 * slice; only `tournament.mode` branches).
 *
 * Functional spec = the OLD APP screen's callbacks, which is the fresher of the
 * two old screens and the only one with arena (line refs are
 * `src/Screens/PlayChess/index.tsx`):
 *   tournament_status_response      L3071-3079
 *   join_tournament_response        L3081-3150
 *   join_tournament_conflict        L3152-3167
 *   withdraw_tournament_response    L3169-3183
 *   pairing                         L3185-3239
 *   tournament_end_response         L3241-3262
 *   tournament_game_end_response    L3411-3447
 *   tournament_starting_soon        L2739-2756
 *   bye_response                    L3058-3069
 * Where the web's `index.js` disagrees, the app wins and the row is recorded in
 * `PlayChessV2/PARITY.md` §9bis.
 *
 * Pure, like every reducer here: one new state object, `now` injected, and the
 * only side effects allowed are the named `../gamePlayStoreWrites` bridges.
 * Three things the old callbacks do that a reducer CANNOT, and that therefore
 * live in the screen root as effects:
 *   - the `gamesEnded` throttle (a 10-15 s jittered timer) — the reducer records
 *     `lastGameEndAt` and `actions.bumpGamesEnded()` is the effect's write;
 *   - the `pairing` timers: the round-start sting, clearing `roundStartAt` after
 *     11 s (L3230-3232) and the 15 s `gamesEnded` bump (L3234-3236);
 *   - the REST refetches (`getTournamentStatus` after a bye, L3060;
 *     `getTournamentDetails` on a 'reload_screen' join echo, L3143-3145).
 */
import {
  bridgeSetArenaIsJoined,
  bridgeSetArenaStatus,
  bridgeSetJoinedTournament,
  bridgeSetJoinedTournamentId,
  bridgeSetTournamentPlay,
} from '../gamePlayStoreWrites';
import {mergeStatus} from './arena';
import {
  extractJoinConflict,
  resolveJoinTnrSuccess,
} from '../chess/tournamentJoin';
import {
  LiveGameState,
  TournamentSlice,
  id,
  initialGame,
  initialTournamentJoin,
  initialWatch,
} from '../state';

export const TOURNAMENT_TYPES = [
  'join_tournament_response',
  'join_tournament_conflict',
  'withdraw_tournament_response',
  'tournament_end_response',
  'tournament_status_response',
  'tournament_game_end_response',
  'pairing',
  'next_round_timer_response',
  'tournament_starting_soon',
  'bye_response',
];

/**
 * The gap between the pairing broadcast and the first move of the new round, as
 * the old screen computes it: 5 s from round 2 on (the players are already on
 * the surface), 10 s for round 1 (L3212-3228). Purely cosmetic — the server
 * owns the real start — and cleared 11 s later by the screen (L3230-3232).
 */
const ROUND_START_DELAY_MS = (round: number): number =>
  round > 1 ? 5000 : 10000;

const withTournament = (
  state: LiveGameState,
  tournament: Partial<TournamentSlice>,
): LiveGameState => ({
  ...state,
  tournament: {...state.tournament, ...tournament},
});

/** the three fields every status/pairing/game-end broadcast carries. */
const broadcastFields = (data: any): Partial<TournamentSlice> => ({
  roundState: data?.roundState ?? null,
  statusMessage: data?.message ?? null,
  ongoingGames:
    data?.ongoing_games === undefined ? null : Number(data.ongoing_games),
});

export function tournamentReducer(
  state: LiveGameState,
  type: string,
  data: any,
  now: number,
): LiveGameState {
  switch (type) {
    case 'tournament_status_response':
      return statusResponse(state, data);
    case 'join_tournament_response':
      return joinResponse(state, data);
    case 'join_tournament_conflict':
      return joinConflict(state, data);
    case 'withdraw_tournament_response':
      return withdrawResponse(state, data);
    case 'pairing':
      return pairing(state, data, now);
    case 'tournament_end_response':
      return tournamentEnd(state, now);
    case 'tournament_game_end_response':
      return tournamentGameEnd(state, data, now);
    case 'tournament_starting_soon':
      return startingSoon(state, data, now);
    case 'bye_response':
      return byeResponse(state, data, now);
    case 'next_round_timer_response':
      return nextRoundTimer(state, data, now);
    default:
      return state;
  }
}

/**
 * `Number(data?.round)` when finite and > 0, else `null` — Fix V2's shared
 * "is this payload telling me a real round number" parse, so a broadcast
 * with no `round` field cannot zero out a round already known.
 */
function roundOf(data: any): number | null {
  const round = Number(data?.round);
  return Number.isFinite(round) && round > 0 ? round : null;
}

/**
 * tournament_status_response — L3071-3079. No tournament-id guard: the old
 * screen has none either, and the socket only joins one tournament room at a
 * time (the previous room is left on an id change, L2218-2236).
 *
 * Fix V2: on a (re)connect or a fresh store this is the ONLY tournament
 * message a client gets (S5 re-sends it), so it now carries the same two
 * fields `tournamentGameEnd`/`pairing` already do: `ended` on
 * `'tournament_finished'`, and the payload's own round. Without them a reload
 * into a finished (or mid-) round left `tabsFor`'s `started` false — the strip
 * still said "Players" — even though the status card, which reads `roundState`
 * directly, already had it right.
 */
function statusResponse(state: LiveGameState, data: any): LiveGameState {
  return withTournament(state, {
    ...broadcastFields(data),
    ended:
      data?.roundState === 'tournament_finished'
        ? true
        : state.tournament.ended,
    currentRound: roundOf(data) ?? state.tournament.currentRound,
  });
}

/**
 * join_tournament_response — L3081-3150.
 *
 * `joinTnrSuccess` (`join.code`) is the old vocabulary, kept because
 * `TournamentPlay` reads it: 0 = nothing to say, 1 = show the failure modal
 * with `join.message`, 2 = joined (or a harmless duplicate join).
 *
 * `resolveJoinTnrSuccess` is the SAME classifier `SocketProvider`'s
 * `GamePlayStore.joinFailure` bridge uses (L804), so the two paths — both of
 * which fire, because the SDK invokes every registered handler — can never
 * disagree.
 *
 * The conflict is only ever CLEARED here, never set: this payload is
 * schemapack-encoded against a fixed 8-field schema that drops `conflict_id`,
 * and the real conflict arrives on its own message EITHER SIDE of this one
 * (L3115-3124). `extractJoinConflict` is still called so a future schema that
 * does carry the keys works without a change.
 */
function joinResponse(state: LiveGameState, data: any): LiveGameState {
  const mine =
    id(data?.playerId) === state.session.me &&
    data?.requestId !== 'reload_screen';

  let join = state.tournament.join;
  if (mine) {
    if (data?.success === true) {
      join = {
        status: 'ok',
        code: 2,
        message: null,
        conflict: null,
        requestId: null,
      };
    } else if (data?.success === false) {
      const code = resolveJoinTnrSuccess(data?.message);
      const inline = extractJoinConflict(data);
      join =
        code === 1
          ? {
              status: 'failed',
              code,
              message: data?.message ?? null,
              conflict: inline || join.conflict,
              requestId: null,
            }
          : {
              status: 'ok',
              code,
              message: null,
              conflict: null,
              requestId: null,
            };
    } else {
      // `success` absent = a transient/echo answer: the old screen shows
      // nothing for it (joinTnrSuccess 0).
      join = {
        status: 'idle',
        code: 0,
        message: null,
        conflict: null,
        requestId: null,
      };
    }
  }

  /**
   * Fix E9: the two CROSS-SCREEN "I am in a tournament" flags (platform-chrome
   * row 31). Only a SUCCESSFUL join of mine raises them — a rejection (overlap
   * guard, closed tournament) must leave the Join button joinable, which is the
   * old web screen's own comment at index.js L1835-1838. The guard is the
   * payload's `playerId` + `success` and NOT `mine`, because `mine` also
   * excludes the 'reload_screen' echo, which the old screen does act on here.
   *
   * No-ops on the app, whose old screen never wrote them (see the "Adapted
   * files" table in `SYNC.md`).
   */
  if (id(data?.playerId) === state.session.me && data?.success === true) {
    bridgeSetJoinedTournament(true);
    bridgeSetJoinedTournamentId(id(data?.tournamentId) || null);
  }

  /**
   * Fix P (2026-10-06): the UI's own membership verdict. Gated on `mine` AND
   * `code === 2` — covering a real success AND the harmless duplicate-join
   * case the same way `join.code` already does — so a reload_screen echo or
   * another player's join can never flip it.
   */
  const confirmed = mine && join.code === 2;

  return withTournament(state, {
    join,
    // A REJECTED join must not bump the count — and the bump is outside the
    // "is it mine" guard on the old screen, because it is the ROOM's count.
    playersJoined:
      data?.success === true
        ? state.tournament.playersJoined + 1
        : state.tournament.playersJoined,
    ...(confirmed
      ? {
          joinedLocal: true,
          membershipVersion: state.tournament.membershipVersion + 1,
          withdraw: {status: 'idle', message: null},
        }
      : {}),
  });
}

/**
 * join_tournament_conflict — L3152-3167. Its own (schema-LESS) message type so
 * the overlap guard's `conflict_id` / `conflict_name` survive; ordering against
 * the response is NOT assumed, which is why this only writes `conflict`.
 */
function joinConflict(state: LiveGameState, data: any): LiveGameState {
  if (id(data?.playerId) !== state.session.me) {
    return state;
  }
  return withTournament(state, {
    join: {...state.tournament.join, conflict: extractJoinConflict(data)},
  });
}

/**
 * withdraw_tournament_response — L3169-3183. In an ARENA it is the inverse of a
 * join: the lobby goes back to the Join surface, so `isJoined` and the personal
 * status are reset (the pause chip and the pairing status must disappear).
 *
 * Fix P (2026-10-06): `withDrawTournamentResMsgSchema` carries `success` /
 * `message` — the old screen (`withdrawPlayerResponseCallback`) never read
 * either and treated every reply as a withdrawal. `failed` is ROOM-WIDE (a
 * refusal — anyone's — must not drop the room's live count, same as a join
 * rejection never bumps it); `mine` still gates everything PERSONAL
 * (`joinedLocal`, `join`, the arena flags, and `withdraw` itself, which must
 * only ever reflect MY OWN attempt, never somebody else's).
 */
function withdrawResponse(state: LiveGameState, data: any): LiveGameState {
  const mine = id(data?.playerId) === state.session.me;
  const isArena = state.tournament.mode === 'arena';
  const failed = data?.success === false;
  const mineSucceeded = mine && !failed;

  // fix E9: the inverse of the join, for ANY family (old web index.js
  // L1898-1900 — the guard there is the payload's playerId, arena or not).
  // Cross-screen chrome flags (`BattleLobby` / `Classroom` read them), not
  // membership. P REVIEW (2026-10-06): gated on `mineSucceeded`, not `mine`
  // — the old screen never branched on success because it had no refusal
  // path at all, but now that `joinedLocal` stays true on a refusal, leaving
  // these cleared would have the two disagree across screens.
  if (mineSucceeded) {
    bridgeSetJoinedTournament(false);
    bridgeSetJoinedTournamentId(null);
  }
  const next = withTournament(state, {
    // a REFUSED withdraw (anyone's) keeps the room's live count.
    playersJoined: failed
      ? state.tournament.playersJoined
      : Math.max(state.tournament.playersJoined - 1, 0),
    withdraw: mine
      ? {
          status: failed ? 'error' : 'ok',
          message: failed ? data?.message ?? null : null,
        }
      : state.tournament.withdraw,
    ...(mineSucceeded
      ? {
          joinedLocal: false,
          membershipVersion: state.tournament.membershipVersion + 1,
          join: {...initialTournamentJoin},
        }
      : {}),
  });
  if (!mineSucceeded || !isArena) {
    return next;
  }
  bridgeSetArenaIsJoined(false);
  bridgeSetArenaStatus({myState: 'idle', paused: false});
  return {
    ...next,
    arena: {
      ...next.arena,
      isJoined: false,
      status: mergeStatus(next.arena.status, {
        myState: 'idle',
        paused: false,
      }),
    },
  };
}

/**
 * pairing — L3185-3239. The round broadcast, and the one message that takes the
 * board away: the old screen calls `setStarted(false)` and pushes the start
 * position (L3238), i.e. the game slice goes back to idle. Whatever board was
 * up belonged to the round that just finished.
 */
function pairing(state: LiveGameState, data: any, now: number): LiveGameState {
  const round = Number(data?.round) || 0;

  // L3196-3197: we are on a tournament surface, so the challenge gate closes.
  bridgeSetTournamentPlay(true);

  return {
    ...withTournament(state, {
      ...broadcastFields(data),
      pairingGenerated: data?.roundState === 'pairings_generated',
      currentRound: round,
      roundStartAt: now + ROUND_START_DELAY_MS(round),
      // the round this was counting down to has started.
      nextRound: null,
    }),
    game: {...initialGame},
    // L3200 setWatchGame(false): a watch cannot survive a new round.
    watch: {...initialWatch},
  };
}

/**
 * next_round_timer_response — the between-rounds countdown (server -> client,
 * `{round, remaining_ms}`), the web screen's own timer source (`index.js`
 * L1924-1927: `setNextRoundNumber(data.round); setRoundStartTime(Date.now() +
 * data.remaining_ms)`). Decoded only from cc-client-sdk >= 2.2.1
 * (`nextRoundTimerResMsgSchema`, `messages.ts` NOTE); on an older install the
 * payload arrives undecoded and `remaining_ms` is not a finite number, so the
 * guard below leaves `nextRound` untouched rather than storing a NaN deadline.
 */
function nextRoundTimer(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  const remainingMs = Number(data?.remaining_ms);
  if (!Number.isFinite(remainingMs)) {
    return withTournament(state, {lastEvent: 'next_round_timer_response'});
  }
  return withTournament(state, {
    lastEvent: 'next_round_timer_response',
    nextRound: {
      round: data?.round == null ? null : Number(data.round),
      startAt: now + remainingMs,
    },
  });
}

/**
 * tournament_end_response — L3241-3262. The old screen also clears `started`
 * and the end modal; V2 does not touch the game slice here (see PARITY §9bis):
 * an ARENA keeps its last games running past the finish broadcast, and
 * `selectRailState` already leaves the board as soon as the game slice is idle.
 */
function tournamentEnd(state: LiveGameState, now: number): LiveGameState {
  // L3253-3256: the tournament is over, so the challenge-blocking gate must be
  // RELEASED. Setting it true here once suppressed challenges for the rest of
  // the session.
  bridgeSetTournamentPlay(false);
  // fix E9: and nobody is "in a tournament" any more — the old web screen
  // clears both flags in the same callback (index.js L1995-1996), outside any
  // "is it mine" guard, because the tournament is over for everyone in it.
  bridgeSetJoinedTournament(false);
  bridgeSetJoinedTournamentId(null);
  return {
    ...withTournament(state, {
      ended: true,
      // fix AB review (2026-10-06): a LIVE, WITNESSED end — the one signal
      // `useRegistrationFlow.ts`'s end-of-tournament modal keys on, never
      // `ended` itself (see the field's own comment in `state.ts`).
      endedAt: now,
      // L3260, verbatim: the counter is a refetch tick for the standings, not a
      // roster size, so the old screen bumps it here too.
      playersJoined: state.tournament.playersJoined + 1,
      // the tournament is over, so no round is still coming.
      nextRound: null,
    }),
    watch: {...initialWatch},
  };
}

/**
 * tournament_game_end_response — L3411-3447. Arrives ~1/s during a round, so
 * everything here has to be cheap: the live counters are updated and the
 * REFETCH tick is only STAMPED (`lastGameEndAt`). The 10-15 s jittered throttle
 * that turns stamps into `gamesEnded` bumps is the screen root's effect —
 * bumping per broadcast caused an API storm across every client in the
 * tournament.
 */
function tournamentGameEnd(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  const finished = data?.roundState === 'tournament_finished';
  return withTournament(state, {
    ...broadcastFields(data),
    pairingGenerated: false,
    ended: finished ? true : state.tournament.ended,
    // fix AB review (2026-10-06): a LIVE, WITNESSED end, same field
    // `tournamentEnd` stamps — the FIRST stamp wins (this broadcast repeats
    // ~1/s for every board in the tournament, so a later one reporting the
    // same `'tournament_finished'` must not push the timestamp forward).
    endedAt: finished
      ? state.tournament.endedAt ?? now
      : state.tournament.endedAt,
    lastGameEndAt: now,
  });
}

/**
 * tournament_starting_soon — L2739-2756. The server REUSES this type as "you
 * are busy elsewhere", so the whole payload is kept and the screen picks the
 * copy (the old screen chooses between the Fun-Battle-busy sheet and the
 * tournament one off `useFunChallengesBattleStore`, which is app state this
 * core cannot read).
 *
 * L2745-2746 also drops the seek and the outgoing challenge: whatever we were
 * waiting for is not going to happen.
 */
function startingSoon(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  return {
    ...withTournament(state, {startingSoon: {at: now, payload: data}}),
    lobby: {...state.lobby, phase: 'idle', seekTableId: null},
    challenge: {...state.challenge, outgoing: null},
  };
}

/**
 * bye_response — L3058-3069. A free point for a round with no opponent.
 *
 * Fix AA (2026-10-06) — a bye is a TOURNAMENT event, not a game. V2 used to
 * fabricate a `game.result` with `endType: 'bye'` so the ordinary end-of-game
 * modal could draw it; the owner, testing it: "when I received a bye
 * suddenly it seemed tournament mode was gone, it looked like a normal game
 * giving the option to review". The old screen pushed a bye through that
 * SAME end-game modal (index.js L3065-3067) only because that modal was its
 * one surface for anything — this lane has its own tournament status card
 * (`rail/tournament/status/tournamentStatus.ts` row 5), which shows it with
 * the rail still in tournament mode. So this reducer writes ONLY the
 * tournament slice: `game` (phase, result, endAckNeeded) is left exactly as
 * it was.
 *
 * NO addressee guard, because none is needed: the server sends this with
 * `io.send(data.player, …)` — a per-username socket lookup, never a room
 * broadcast (`timerProcessor.js` L1360 → `cc-conn-mgr` `routing.js`
 * `send(username, packet)`), so it only ever reaches its recipient. (`player`
 * IS a real user id — `match.player.cc_userid`, L1249 — not the pairing-row
 * id; the 1:1 guarantee comes from the transport, not from the id's shape.)
 */
function byeResponse(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  // fix AA review (2026-10-06): `Number(data.round)` alone turned a
  // malformed payload (e.g. a non-numeric string) into `NaN`, which then
  // failed every `round === R` / `round > x` comparison silently rather than
  // reading as "no round" the way a MISSING field already does. `null` and
  // `''` are caught BEFORE the `Number(...)` call for the same reason:
  // `Number(null)` and `Number('')` are both `0`, a FINITE number, which
  // would otherwise read as the real round 0.
  const roundField = data?.round;
  const n = roundField == null || roundField === '' ? NaN : Number(roundField);
  const round = Number.isFinite(n) ? n : null;
  return withTournament(state, {
    bye: {round, at: now},
    // T2.1(b): a bye is always MINE (1:1, no addressee guard above) —
    // membership is PROVEN by playing a round, and a bye is a round played.
    // Never flip a `false` (fix P: a withdrawn player can still finish a
    // live game).
    joinedLocal:
      state.tournament.joinedLocal === null
        ? true
        : state.tournament.joinedLocal,
    // fix AA: a bye for round N is proof round N is the current one — never
    // LOWER it (a stray/duplicate/out-of-order bye for an earlier round must
    // not undo a round the client already knows is current); `round === null`
    // (no round on the payload) leaves it untouched.
    currentRound:
      round !== null && round > (state.tournament.currentRound ?? 0)
        ? round
        : state.tournament.currentRound,
  });
}
